import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Package, ShoppingCart, Users, AlertTriangle, TrendingUp, DollarSign, Clock, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#1a365d", "#d4af37", "#38bdf8", "#94a3b8", "#0f172a"];

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const [stats, setStats] = useState(null);
  const [dailySales, setDailySales] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [statsRes, dailyRes, categoryRes, topRes, ordersRes, lowStockRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers }),
          axios.get(`${API}/sales/daily?days=14`, { headers }),
          axios.get(`${API}/sales/by-category`, { headers }),
          axios.get(`${API}/sales/top-products?limit=5`, { headers }),
          axios.get(`${API}/orders`, { headers }),
          axios.get(`${API}/inventory/low-stock`, { headers }),
        ]);
        
        setStats(statsRes.data);
        setDailySales(dailyRes.data);
        setCategorySales(categoryRes.data);
        setTopProducts(topRes.data);
        setRecentOrders(ordersRes.data.slice(0, 5));
        setLowStock(lowStockRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500",
      processing: "bg-blue-500",
      out_for_delivery: "bg-purple-500",
      delivered: "bg-green-500",
      cancelled: "bg-red-500",
    };
    return colors[status] || "bg-gray-500";
  };

  const kpiCards = [
    { label: t("todaysRevenue"), value: `${stats?.today_revenue?.toFixed(2) || 0} ${t("lyd")}`, icon: DollarSign, color: "bg-green-500", change: "+12%" },
    { label: t("monthlyRevenue"), value: `${stats?.month_revenue?.toFixed(2) || 0} ${t("lyd")}`, icon: TrendingUp, color: "bg-[#d4af37]" },
    { label: t("todaysOrders"), value: stats?.today_orders || 0, icon: ShoppingCart, color: "bg-blue-500" },
    { label: t("pendingOrders"), value: stats?.pending_orders || 0, icon: Clock, color: "bg-yellow-500" },
    { label: t("totalProducts"), value: stats?.total_products || 0, icon: Package, color: "bg-purple-500" },
    { label: t("lowStockItems"), value: stats?.low_stock_count || 0, icon: AlertTriangle, color: "bg-red-500" },
    { label: t("totalCustomers"), value: stats?.total_customers || 0, icon: Users, color: "bg-[#1a365d]" },
    { label: t("monthlyOrders"), value: stats?.month_orders || 0, icon: CheckCircle, color: "bg-teal-500" },
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-32 bg-slate-800 animate-pulse rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-slate-800 animate-pulse rounded-xl" />
          <div className="h-80 bg-slate-800 animate-pulse rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <Card key={kpi.label} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${kpi.color}`}>
                  <kpi.icon className="w-5 h-5 text-white" />
                </div>
                {kpi.change && (
                  <Badge className="bg-green-500/20 text-green-400 border-0">
                    {kpi.change}
                  </Badge>
                )}
              </div>
              <p className="text-2xl font-bold text-white">{kpi.value}</p>
              <p className="text-sm text-slate-400">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Sales Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-heading">{t("dailySales")} ({t("last14Days")})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailySales}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickFormatter={(v) => v.slice(5)} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "8px" }}
                    labelStyle={{ color: "#f8fafc" }}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#d4af37" strokeWidth={2} dot={{ fill: "#d4af37" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Sales Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-heading">{t("salesByCategory")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categorySales}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="revenue"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {categorySales.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "8px" }}
                    formatter={(value) => [`${value} ${t("lyd")}`, t("totalRevenue")]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Products & Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-heading">{t("topSellingProducts")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={100} tickFormatter={(v) => v.slice(0, 15)} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "8px" }}
                  />
                  <Bar dataKey="revenue" fill="#1a365d" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-heading">{t("recentOrders")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentOrders.length === 0 ? (
                <p className="text-slate-400 text-center py-8">{t("noOrders")}</p>
              ) : (
                recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                    <div>
                      <p className="font-medium text-white">#{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-slate-400">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-white">{order.total} {t("lyd")}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(order.status)}`} />
                        <span className="text-xs text-slate-400 capitalize">{order.status.replace("_", " ")}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alerts */}
      {lowStock.length > 0 && (
        <Card className="bg-red-900/20 border-red-800">
          <CardHeader>
            <CardTitle className="text-red-400 font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {t("lowStockAlerts")} ({lowStock.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lowStock.slice(0, 6).map((product) => (
                <div key={product.id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{product.name}</p>
                    <p className="text-sm text-slate-400">SKU: {product.sku || "N/A"}</p>
                  </div>
                  <Badge className="bg-red-500/20 text-red-400 border-0">
                    {product.stock_quantity} {t("left")}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
